import { axiosApiInstance } from 'Store';
import { stringToNumber } from 'utils/formatters/stringToNumber';
import {
    PredictionHistoryDetailsDto,
    PredictionStatusDto,
    PredictionStatusDtoStatusEnum,
    PredictPriceDataDto,
    PredictPriceDataFullDto,
    PriceRangeResponseDto,
    UpdatePredictionTitleDto,
    UserPredictionListItemDto,
    ValuationResultDto
} from 'utils/types/AiPredictorModels';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

const getPriceRange = async (requestFilterState: PredictPriceDataDto): Promise<PriceRangeResponseDto | null> => {
    const requestData: PredictPriceDataDto = {
        area: stringToNumber(requestFilterState.area) || 0,
        numberOfRooms: requestFilterState.numberOfRooms
            ? requestFilterState.numberOfRooms
            : 0,
        builtYear: stringToNumber(requestFilterState.builtYear) || 0,
        buildingType: requestFilterState.buildingType,
        buildingCondition: requestFilterState.buildingCondition,
        buildingOverallCondition: requestFilterState.buildingOverallCondition,
        floor: requestFilterState.floor ? requestFilterState.floor : 0,
        floors: requestFilterState.floors ? requestFilterState.floors : 0,
        typeOfMarket: requestFilterState.typeOfMarket,
        description: requestFilterState.description || '',
        buildingNumber: requestFilterState.buildingNumber || '',
        id: requestFilterState.id
    };

    return axiosApiInstance({
        method: 'post',
        url: '/ai-predictor/predict/price/range',
        data: requestData
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'getPriceRange',
                request: { url: '/ai-predictor/predict/price/range' }
            });
            activeNotification(
                'Ups, coś poszło nie tak',
                'Spróbuj jeszcze raz.',
                'warning'
            );

            return null;
        });
};

const createPrediction = async (requestData: PredictPriceDataFullDto): Promise<{ uuid: string } | { errorStatus: number } | null> => {
    return axiosApiInstance({
        method: 'post',
        url: '/ai-predictor/predict/price/create',
        data: requestData
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            if (err.response?.status === 403) {
                return { errorStatus: 403 };
            }

            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'createPrediction',
                request: { url: '/ai-predictor/predict/price/create' }
            });
            activeNotification(
                'Ups, coś poszło nie tak',
                'Spróbuj jeszcze raz.',
                'warning'
            );

            return null;
        });
};

const checkPredictionStatus = async (uuid: string): Promise<PredictionStatusDto | null> => {
    return axiosApiInstance
        .get(`/ai-predictor/predict/price/status/${uuid}`)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'checkPredictionStatus',
                request: { url: `/ai-predictor/predict/price/status/${uuid}` }
            });

            return null;
        });
};

const getPredictionResultV2 = async (uuid: string): Promise<ValuationResultDto | null> => {
    return axiosApiInstance
        .get(`/ai-predictor/predict/price/find/v2/${uuid}`)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'getPredictionResultV2',
                request: { url: `/ai-predictor/predict/price/find/v2/${uuid}` }
            });

            return null;
        });
};

export interface PredictionValuationResponse {
    success: boolean;
    data: ValuationResultDto | null;
}

const getPredictionValuation = async (
    uuid: string,
    onStatusUpdate?: (status: PredictionStatusDtoStatusEnum) => void
): Promise<PredictionValuationResponse> => {
    const POLL_INTERVAL = 500;
    const MAX_RETRIES = 360;
    let retryCount = 0;

    return new Promise((resolve) => {
        const poll = async () => {
            const statusResponse = await checkPredictionStatus(uuid);

            if (!statusResponse) {
                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    setTimeout(poll, POLL_INTERVAL);
                } else {
                    activeNotification(
                        'Ups, coś poszło nie tak',
                        'Przekroczono limit czasu oczekiwania.',
                        'warning'
                    );
                    resolve({ success: false, data: null });
                }

                return;
            }

            const { status } = statusResponse;

            if (status && onStatusUpdate) {
                onStatusUpdate(status);
            }

            if (status === PredictionStatusDtoStatusEnum.COMPLETED) {
                const result = await getPredictionResultV2(uuid);

                if (result) {
                    resolve({ success: true, data: result });
                } else {
                    resolve({ success: false, data: null });
                }
            } else if (status === PredictionStatusDtoStatusEnum.FAILED) {
                activeNotification(
                    'Błąd wyceny',
                    'Wycena nie powiodła się. Spróbuj ponownie.',
                    'warning'
                );
                resolve({ success: false, data: null });
            } else if (retryCount >= MAX_RETRIES) {
                activeNotification(
                    'Ups, coś poszło nie tak',
                    'Przekroczono limit czasu oczekiwania.',
                    'warning'
                );
                resolve({ success: false, data: null });
            } else {
                retryCount++;
                setTimeout(poll, POLL_INTERVAL);
            }
        };

        poll();
    });
};

const getUserPredictions = async (page = 0, size = 50): Promise<UserPredictionListItemDto[]> => {
    return axiosApiInstance
        .get('/ai-predictor/history/list', {
            params: { page, size }
        })
        .then((resp) => {
            return resp.data?.content || resp.data || [];
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'getUserPredictions',
                request: { url: '/ai-predictor/history/list' }
            });

            return [];
        });
};

const getPredictionDetails = async (uuid: string): Promise<PredictionHistoryDetailsDto | null> => {
    return axiosApiInstance
        .get(`/ai-predictor/history/${uuid}/details`)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'getPredictionDetails',
                request: { url: `/ai-predictor/history/${uuid}/details` }
            });
            activeNotification(
                'Ups, coś poszło nie tak',
                'Nie udało się pobrać szczegółów raportu.',
                'warning'
            );

            return null;
        });
};

const deletePrediction = async (uuid: string): Promise<boolean> => {
    return axiosApiInstance
        .delete(`/ai-predictor/history/${uuid}`)
        .then(() => {
            activeNotification('Usunięto wycenę', 'Wycena została usunięta.', 'success');

            return true;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'deletePrediction',
                request: { url: `/ai-predictor/history/${uuid}` }
            });
            activeNotification('Ups, coś poszło nie tak', 'Nie udało się usunąć wyceny.', 'warning');

            return false;
        });
};

const updatePredictionTitle = async (uuid: string, newTitle: string): Promise<UserPredictionListItemDto | null> => {
    const data: UpdatePredictionTitleDto = { newTitle };

    return axiosApiInstance
        .patch(`/ai-predictor/history/${uuid}/title`, data)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, {
                functionName: 'updatePredictionTitle',
                request: { url: `/ai-predictor/history/${uuid}/title` }
            });
            activeNotification(
                'Ups, coś poszło nie tak',
                'Nie udało się zaktualizować tytułu.',
                'warning'
            );

            return null;
        });
};

export const propertyValuationApiRequests = {
    getPriceRange,
    createPrediction,
    checkPredictionStatus,
    getPredictionResultV2,
    getPredictionValuation,
    getUserPredictions,
    getPredictionDetails,
    updatePredictionTitle,
    deletePrediction
};
